import {
	Text,
	HStack,
	Button,
	VStack,
	H2,
	Tooltip,
	Select,
	SelectOptionType,
} from '@montel/montelpro-shared-components-v2';
import { SUBSCRIPTIONS_API_ROUTE } from 'apiRoutes';
import { Provider } from 'domain/subscription/interfaces/Subscription';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDefaultProviderBlacklistContext } from './DefaultProviderBlacklistContext';
import { useGet } from 'hooks/useGet';
import SectionDivider from 'components/common/SectionDivider';
import { DefaultProviderBlacklistTable } from './DefaultProviderBlacklistTable';
import useAuthRoles from 'hooks/useAuthRoles';
import { AdminRole } from 'domain/account/enums/AdminRole';

const toProviderOptions = (providers: Provider[]): SelectOptionType[] =>
	providers.map((p) => ({
		value: p.id,
		label: `${p.name} (${p.code})`,
	}));

export const EditDefaultProviderBlacklist: FunctionComponent = () => {
	const [selectedProviderOption, setSelectedProviderOption] =
		useState<SelectOptionType | null>(null);

	const { adminIsRestrictedToRole } = useAuthRoles();

	const {
		allProviders,
		blacklistedProviders,
		originalBlacklistedProviders,
		addedProviders,
		removedProviders,
		isLoading,
		setBlacklistedProviders,
		setOriginalBlacklistedProviders,
		addProvider,
		removeProvider,
		resetChanges,
		submitChanges,
	} = useDefaultProviderBlacklistContext();

	const { data: blacklistedProviderData, getData: getBlacklistedProviders } =
		useGet<string[]>(
			`${SUBSCRIPTIONS_API_ROUTE}/GetDefaultProviderBlacklist`,
		);

	useEffect(() => {
		if (blacklistedProviderData) {
			const providers = blacklistedProviderData
				.map((code) => allProviders?.find((p) => p.code === code))
				.filter((p): p is Provider => p !== undefined);

			setBlacklistedProviders(providers);
			setOriginalBlacklistedProviders(providers);
		}
	}, [blacklistedProviderData, allProviders]);

	useEffect(() => {
		getBlacklistedProviders();
	}, []);

	const onProviderSelected = (option: SelectOptionType) => {
		const provider = allProviders?.find((p) => p.id === option.value);

		if (provider) {
			addProvider(provider);
			setSelectedProviderOption(null);
		}
	};

	const onSubmit = async () => {
		await submitChanges();
		getBlacklistedProviders();
	};

	const selectableProviders = allProviders?.filter(
		(p) =>
			!blacklistedProviders.some((bp) => bp.id === p.id) &&
			!addedProviders.some((ap) => ap.id === p.id),
	);

	const isDirty = addedProviders.length > 0 || removedProviders.length > 0;
	const disableEditBlacklist = adminIsRestrictedToRole(AdminRole.SALES);

	return (
		<VStack gap="lg" fullWidth>
			<SectionDivider />
			<HStack align="center" gap="xs">
				<H2>Default provider blacklist</H2>
				{isDirty && (
					<Tooltip
						content="You have unsaved changes. Remember to save or undo your changes before navigating away from this page."
						component={
							<Text as="span" color="bgSuccess">
								(unsaved changes)
							</Text>
						}
					>
						<VStack gap="xs">
							{addedProviders.length > 0 && (
								<Text>{addedProviders.length} added</Text>
							)}
							{removedProviders.length > 0 && (
								<Text>{removedProviders.length} removed</Text>
							)}
						</VStack>
					</Tooltip>
				)}
			</HStack>
			<HStack gap="md" fullWidth>
				<Button
					text="Save changes"
					size="sm"
					type="submit"
					isLoading={isLoading}
					loadingText="Updating..."
					onClick={onSubmit}
					isDisabled={isLoading || !isDirty || disableEditBlacklist}
				/>
				<Button
					text="Undo changes"
					size="sm"
					variant="tertiary"
					onClick={resetChanges}
					isDisabled={isLoading || !isDirty || disableEditBlacklist}
				/>
			</HStack>
			<VStack gap="lg" fullWidth>
				{blacklistedProviders.length ? (
					<DefaultProviderBlacklistTable
						blacklistedProviders={blacklistedProviders}
						removeProvider={removeProvider}
						addedProviders={addedProviders}
						disabled={disableEditBlacklist}
					/>
				) : (
					<NoProvidersSelectedText
						originalBlacklistedProviders={
							originalBlacklistedProviders
						}
					/>
				)}
				{allProviders &&
					selectableProviders &&
					!disableEditBlacklist && (
						<VStack gap="md" fullWidth>
							<Text>
								Add a provider to the blacklist by selecting it
								from the dropdown below:
							</Text>
							<Select
								label=""
								options={toProviderOptions(selectableProviders)}
								value={selectedProviderOption}
								placeholder="Select a provider..."
								onChange={onProviderSelected}
							/>
						</VStack>
					)}
			</VStack>
			<SectionDivider />
		</VStack>
	);
};

const NoProvidersSelectedText: React.FC<{
	originalBlacklistedProviders: Provider[];
}> = ({ originalBlacklistedProviders }) => {
	if (originalBlacklistedProviders.length > 0) {
		return (
			<Text fontStyle="italic" color="textError">
				All providers will be removed from the blacklist.
			</Text>
		);
	}

	return (
		<Text fontStyle="italic">No providers are blacklisted by default.</Text>
	);
};
